import {
    DataGrid,
    GridColumnsConfigProvider,
    Input,
    toInt,
    WorkplaceBtnActions,
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DataTimeText from 'components/Common/DataTime';
import Money from 'components/Common/Money';
import OrderCancelPopover from 'components/Workplaces/Order/Popovers/OrderCancelPopover';
import useAuth from 'hooks/useAuth';
import { LoanInfo, OrderInfo, StatusHist } from 'models/api/order';
import { useOrderInfoContext } from 'providers/OrderInfoProvider';
import { Dispatch, RefObject, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    FormGroup,
    Row,
    UncontrolledAccordion,
} from 'reactstrap';
import EditPopover from '../LoanControlButtons/EditPopover';
import UndoRedoPopover from '../LoanControlButtons/UndoRedoPopover';
import WriteOffButton from '../LoanControlButtons/WriteOffButton';
import '../OrderInfo.scss';
import AccrualScheduleTable from '../Tables/AccrualScheduleTable';
import LoanParametersTable from '../Tables/LoanParametesTable';
import OrderCalcsTable from '../Tables/OrderCalcsTable';
import ProductsTable from '../Tables/ProductsTable';
import ProlongationsTable from '../Tables/ProlongationsTable';

const OrderTab = ({
    orderInfoQueryFn,
    isOpen,
    container,
}: {
    orderInfoQueryFn: () => Promise<OrderInfo>;
    isOpen: boolean;
    container?: RefObject<HTMLElement>;
}) => {
    const { t } = useTranslation();
    const { hasRole } = useAuth();
    const [onlyActiveLoans, setOnlyActiveLoans] = useState(true);
    const [orderInfo, setOrderInfo] = useState<OrderInfo | null>();
    const [wasOpened, setWasOpened] = useState(false);

    useEffect(() => {
        !wasOpened && setWasOpened(isOpen);
    }, [isOpen]);

    const { isFetching } = useQuery('orderInfo', () => orderInfoQueryFn(), {
        onSuccess: (data) => setOrderInfo(data),
        enabled: wasOpened,
    });

    const accordionProps = {
        defaultOpen: ['order', 'loan'],
        stayOpen: true,
        toggle: () => undefined,
    };

    const loanInfo = useMemo(
        () =>
            orderInfo
                ? onlyActiveLoans
                    ? orderInfo.loans.filter((x) => x.statusCode !== 'cancel')
                    : orderInfo.loans
                : [],
        [onlyActiveLoans, orderInfo]
    );

    //TODO переводы
    return (
        <Container>
            <UncontrolledAccordion {...accordionProps} flush={false}>
                <AccordionItem>
                    <AccordionHeader targetId="order">
                        {t('BackOfficeOrderingInformation')}
                    </AccordionHeader>
                    <AccordionBody accordionId="order">
                        <Row>
                            <Col>
                                <Card className="h-100" outline>
                                    <CardHeader>{t('BackOfficeStatusChangeHistory')}</CardHeader>
                                    <CardBody>
                                        <GridColumnsConfigProvider<StatusHist>
                                            storagePath="status-hist-grid"
                                            initColumns={[
                                                {
                                                    key: 'status',
                                                    name: <Trans i18nKey={'BackOfficeStatus'} />,
                                                    cellClass: 'base-data-grid__normal-line-height',
                                                    formatter: ({ row }) => (
                                                        <div>
                                                            <div>{row.status}</div>
                                                            <div>
                                                                <small className="text-muted">
                                                                    {row.operatorFio}
                                                                </small>
                                                            </div>
                                                            <div>
                                                                <small className="text-muted">
                                                                    <DataTimeText value={row.sd} />
                                                                </small>
                                                            </div>
                                                        </div>
                                                    ),
                                                },
                                                {
                                                    key: 'reason',
                                                    cellClass: 'base-data-grid__normal-line-height',
                                                    name: <Trans i18nKey={'BackOfficeReason'} />,
                                                    formatter: ({ row }) => (
                                                        <div>
                                                            <div>{row.reason}</div>
                                                            <div>
                                                                <small className="text-muted">
                                                                    {row.dsc}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    ),
                                                },
                                            ]}
                                        >
                                            <DataGrid<StatusHist>
                                                reactDataGreedProps={{
                                                    defaultColumnOptions: {
                                                        sortable: false,
                                                        resizable: true,
                                                    },
                                                }}
                                                lineHeight={60}
                                                loading={isFetching}
                                                rows={orderInfo?.statusHist ?? []}
                                            />
                                        </GridColumnsConfigProvider>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col>
                                <Card outline>
                                    <CardHeader>{t('BackOfficeLoanParameters')}</CardHeader>
                                    <CardBody>
                                        <LoanParametersTable
                                            model={orderInfo}
                                            loading={isFetching}
                                            wasOpened={wasOpened}
                                        />
                                    </CardBody>
                                </Card>
                                {orderInfo && orderInfo.activeLoan && (
                                    <Card className="mt-3" outline>
                                        <CardHeader>{t('BackOfficeSummaryInfo')}</CardHeader>
                                        <CardBody>
                                            <table className="table table-sm mb-0">
                                                <tbody>
                                                    <tr>
                                                        <td>{t('BackOfficeBalance')}</td>
                                                        <td
                                                            className={
                                                                isFetching
                                                                    ? 'loading td_loading'
                                                                    : ''
                                                            }
                                                        >
                                                            <Money
                                                                value={orderInfo.activeLoan.balance}
                                                            />
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>{t('BackOfficeDebtAmount')}</td>
                                                        <td
                                                            className={
                                                                isFetching
                                                                    ? 'loading td_loading'
                                                                    : ''
                                                            }
                                                        >
                                                            <Money
                                                                value={
                                                                    orderInfo.activeLoan.debtAmount
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                    {orderInfo.paymentAmountsForProlongation.map(
                                                        (x, idx) => (
                                                            <tr key={idx}>
                                                                <td>
                                                                    {t(
                                                                        'BackOfficeAmountsExtendedByNDays',
                                                                        [x.prolongationTerm]
                                                                    )}
                                                                </td>
                                                                <td
                                                                    className={
                                                                        isFetching
                                                                            ? 'loading td_loading'
                                                                            : ''
                                                                    }
                                                                >
                                                                    <Money value={x.prolongCost} />
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </CardBody>
                                    </Card>
                                )}
                            </Col>
                        </Row>
                    </AccordionBody>
                </AccordionItem>
                {orderInfo && orderInfo.products && (
                    <AccordionItem>
                        <AccordionHeader targetId="products">
                            {t('BackOfficeProducts')}
                        </AccordionHeader>
                        <AccordionBody accordionId="products">
                            <ProductsTable products={orderInfo.products}></ProductsTable>
                        </AccordionBody>
                    </AccordionItem>
                )}
                {orderInfo && orderInfo.calcs && orderInfo.loanPayments && (
                    <AccordionItem>
                        <AccordionHeader targetId="loan">{t('BackOfficeLoanInfo')}</AccordionHeader>
                        <AccordionBody accordionId="loan">
                            {orderInfo.loans.length > 0 && (
                                <>
                                    <div className="mb-3">
                                        <Input
                                            type="checkbox"
                                            name="notUsed"
                                            checked={onlyActiveLoans}
                                            onChange={(e) => setOnlyActiveLoans(e.target.checked)}
                                            label={t('BackOfficeActiveLoans')}
                                        />
                                    </div>
                                    <FormGroup>
                                        <GridColumnsConfigProvider<LoanInfo>
                                            storagePath="loans-grid"
                                            initColumns={[
                                                {
                                                    key: 'id',
                                                    name: <Trans i18nKey={'BackOfficeId'} />,
                                                    cellClass: 'base-data-grid__normal-line-height',
                                                    formatter: ({ row }) => (
                                                        <div>
                                                            <div>{row.id}</div>
                                                            <div>
                                                                <small className="text-muted">
                                                                    <DataTimeText value={row.sd} />
                                                                </small>
                                                            </div>
                                                        </div>
                                                    ),
                                                },
                                                {
                                                    key: 'statusName',
                                                    draggable: false,
                                                    name: <Trans i18nKey={'BackOfficeStatus'} />,
                                                    cellClass: 'base-data-grid__normal-line-height',
                                                    formatter: ({ row }) => (
                                                        <div>
                                                            <div>{row.statusName}</div>
                                                            <div>
                                                                <small className="text-muted">
                                                                    <DataTimeText
                                                                        value={row.statusDt}
                                                                    />
                                                                </small>
                                                            </div>
                                                        </div>
                                                    ),
                                                },
                                                {
                                                    key: 'loanAccountNo',
                                                    draggable: false,
                                                    cellClass: 'base-data-grid__normal-line-height',
                                                    name: (
                                                        <Trans i18nKey={'BackOfficeLoanAccount'} />
                                                    ),
                                                },
                                                {
                                                    key: 'amount',
                                                    draggable: false,
                                                    cellClass: 'base-data-grid__normal-line-height',
                                                    name: (
                                                        <Trans i18nKey={'BackOfficeLoanAmount'} />
                                                    ),
                                                    formatter: ({ row }) => (
                                                        <Money value={row.amount} />
                                                    ),
                                                },
                                                {
                                                    key: 'dueDt',
                                                    draggable: false,
                                                    cellClass: 'base-data-grid__normal-line-height',
                                                    name: <Trans i18nKey={'BackOfficeDueDate'} />,
                                                    formatter: ({ row }) => (
                                                        <DataTimeText value={row.dueDt} />
                                                    ),
                                                },
                                                {
                                                    key: 'balance',
                                                    draggable: false,
                                                    cellClass: 'base-data-grid__normal-line-height',
                                                    name: <Trans i18nKey={'BackOfficeBalance'} />,
                                                    formatter: ({ row }) => (
                                                        <Money value={row.balance} />
                                                    ),
                                                },
                                                {
                                                    key: 'debtAmount',
                                                    draggable: false,
                                                    cellClass: 'base-data-grid__normal-line-height',
                                                    name: (
                                                        <Trans i18nKey={'BackOfficeDebtAmount'} />
                                                    ),
                                                    formatter: ({ row }) => (
                                                        <Money value={row.debtAmount} />
                                                    ),
                                                },
                                                {
                                                    key: 'actions',
                                                    name: (
                                                        <FontAwesomeIcon icon={faBoltLightning} />
                                                    ),
                                                    cellClass:
                                                        'base-data-grid__normal-line-height base-data-grid__cell_centered no-change-modal',
                                                    headerCellClass:
                                                        'base-data-grid__cell_centered',
                                                    width: 70,
                                                    minWidth: 70,
                                                    visible:
                                                        orderInfo.loans.some(
                                                            (x) => x.statusCode !== 'cancel'
                                                        ) ||
                                                        orderInfo.loans.some(
                                                            (x) => x.isLoanWriteOffPossible
                                                        ) ||
                                                        (hasRole([
                                                            'orderGetLoanDetailInfo',
                                                            'orderLoanWriteOff',
                                                            'orderGetBillRollbackInfo',
                                                            'orderBillRollback',
                                                            'orderBillRollup',
                                                            'orderCancel',
                                                            'orderGetBillRefundInfo',
                                                        ]) &&
                                                            orderInfo.loans.some(
                                                                (x) => x.statusCode !== 'cancel'
                                                            )) ||
                                                        orderInfo.loans.some(
                                                            (x) =>
                                                                x.maxRefundAmount &&
                                                                x.maxRefundAmount > 0
                                                        ),
                                                    clickDisable: true,
                                                    formatter: ({ row }) => (
                                                        <LoansActionButtons
                                                            row={row}
                                                            container={container}
                                                            setOrderInfo={setOrderInfo}
                                                            isFetching={isFetching}
                                                        />
                                                    ),
                                                },
                                            ]}
                                        >
                                            <DataGrid<LoanInfo>
                                                reactDataGreedProps={{
                                                    defaultColumnOptions: {
                                                        sortable: false,
                                                        resizable: true,
                                                    },
                                                }}
                                                lineHeight={45}
                                                rows={loanInfo}
                                                blockSize={
                                                    60 + (isFetching ? 2 : loanInfo.length) * 45
                                                }
                                                minBlockSize={60}
                                                loading={isFetching}
                                            />
                                        </GridColumnsConfigProvider>
                                    </FormGroup>
                                </>
                            )}
                            <OrderCalcsTable calcs={orderInfo.calcs} loading={isFetching} />
                            <ProlongationsTable prolongations={orderInfo.prolongations} />
                            <AccrualScheduleTable
                                loanPayments={orderInfo.loanPayments}
                                loading={isFetching}
                            />
                        </AccordionBody>
                    </AccordionItem>
                )}
            </UncontrolledAccordion>
        </Container>
    );
};

const LoansActionButtons = ({
    row,
    container,
    isFetching,
    setOrderInfo,
}: {
    row: LoanInfo;
    container?: RefObject<HTMLElement>;
    setOrderInfo: Dispatch<SetStateAction<OrderInfo | null | undefined>>;
    isFetching: boolean;
}) => {
    const { hasRole } = useAuth();
    const { orderDetails } = useOrderInfoContext();

    const setRollbackState = (value: boolean) =>
        setOrderInfo((prev) =>
            prev
                ? {
                      ...prev,
                      loans: prev.loans.map((x) =>
                          x.id === row.id ? { ...x, isLoanInRollbackState: value } : x
                      ),
                  }
                : prev
        );

    return (
        <WorkplaceBtnActions menuContainer={container}>
            {hasRole('orderGetLoanDetailInfo') && !isFetching && (
                <EditPopover
                    statusCode={row.statusCode}
                    isLoanEditPossible={row.isLoanEditPossible}
                    id={toInt(row.id) ?? 0}
                    key="edit-loan-info"
                />
            )}
            {hasRole('orderLoanWriteOff') && (
                <WriteOffButton
                    isLoanWriteOffPossible={row.isLoanWriteOffPossible}
                    id={row?.id ?? 0}
                    key="loan-write-off"
                />
            )}
            {hasRole(['orderGetBillRollbackInfo', 'orderBillRollback', 'orderBillRollup']) && (
                <UndoRedoPopover
                    statusCode={row.statusCode}
                    id={toInt(row.id) ?? 0}
                    isLoanInRollbackState={row.isLoanInRollbackState}
                    setRollbackState={setRollbackState}
                    isDefaultRollbackDt={!!row.defaultRollbackDt}
                    key="bill-undo-redo"
                />
            )}
            {hasRole('orderCancel') && (
                <OrderCancelPopover
                    orderId={`${orderDetails?.id}`}
                    key="cancel-order"
                    currentStatus={orderDetails?.orderGeneral.statusCode ?? ''}
                />
            )}
        </WorkplaceBtnActions>
    );
};

export default OrderTab;
